import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Q = C · ΔP^n — log-log regression of multi-point blower door data. */
export function DiagramBETRegression() {
  // Axes
  const ox = 55, oy = 215;
  const axw = 260, axh = 180;

  // Data points (ΔP Pa → Q CFM on log-log). Tight scatter around line slope n≈0.65.
  const points: Array<[number, number]> = [
    [70, 195], [100, 178], [130, 160], [162, 142], [195, 125], [225, 108], [258, 92], [290, 75],
  ];
  // Regression line endpoints
  const lineStart: [number, number] = [60, 205];
  const lineEnd: [number, number] = [305, 60];

  return (
    <DiagramCanvas>
      {/* Background card */}
      <Rect x={20} y={20} width={310} height={215} fill="rgba(0,0,0,0.3)" stroke={DT.dim} strokeWidth="0.6" />

      {/* Axes */}
      <Line x1={ox} y1={oy} x2={ox + axw} y2={oy} stroke={DT.dim} strokeWidth="1.4" />
      <Line x1={ox} y1={oy} x2={ox} y2={oy - axh} stroke={DT.dim} strokeWidth="1.4" />

      {/* Log grid lines */}
      {[0.25, 0.5, 0.75].map((t, i) => (
        <G key={i}>
          <Line x1={ox + axw * t} y1={oy - axh} x2={ox + axw * t} y2={oy}
            stroke={DT.grid} strokeWidth="0.6" strokeDasharray="2,3" />
          <Line x1={ox} y1={oy - axh * t} x2={ox + axw} y2={oy - axh * t}
            stroke={DT.grid} strokeWidth="0.6" strokeDasharray="2,3" />
        </G>
      ))}

      {/* Axis ticks with log pressures */}
      {[['10', 0], ['25', 0.3], ['50', 0.58], ['100', 0.85], ['', 1]].map(([lbl, t], i) => (
        <G key={i}>
          <Line x1={ox + axw * (t as number)} y1={oy} x2={ox + axw * (t as number)} y2={oy + 4} stroke={DT.dim} strokeWidth="0.8" />
          {lbl && <SvgText x={ox + axw * (t as number)} y={oy + 14} fill={DT.dim} fontSize="7" fontFamily={DT.mono} textAnchor="middle">{lbl as string}</SvgText>}
        </G>
      ))}
      {[['10', 0.05], ['100', 0.35], ['1k', 0.65], ['10k', 0.95]].map(([lbl, t], i) => (
        <G key={`y${i}`}>
          <Line x1={ox - 4} y1={oy - axh * (t as number)} x2={ox} y2={oy - axh * (t as number)} stroke={DT.dim} strokeWidth="0.8" />
          <SvgText x={ox - 6} y={oy - axh * (t as number) + 3} fill={DT.dim} fontSize="7" fontFamily={DT.mono} textAnchor="end">{lbl as string}</SvgText>
        </G>
      ))}

      <SvgText x={ox + axw / 2} y={oy + 26} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">log ΔP (Pa) →</SvgText>
      <SvgText x={ox - 34} y={oy - axh / 2} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle"
        rotation="-90" originX={ox - 34} originY={oy - axh / 2}>log Q (CFM)</SvgText>

      {/* Regression line */}
      <Line x1={lineStart[0]} y1={lineStart[1]} x2={lineEnd[0]} y2={lineEnd[1]} stroke={DT.leader} strokeWidth="2.2" />

      {/* Data points */}
      {points.map(([px, py], i) => (
        <G key={i}>
          <Circle cx={px} cy={py} r={5} fill={DT.varColor} opacity={0.3} />
          <Circle cx={px} cy={py} r={3} fill={DT.varColor} />
        </G>
      ))}

      {/* Slope triangle (rise/run) */}
      <Line x1={170} y1={140} x2={230} y2={140} stroke={DT.varColor} strokeWidth="1" strokeDasharray="2,2" />
      <Line x1={230} y1={140} x2={230} y2={108} stroke={DT.varColor} strokeWidth="1" strokeDasharray="2,2" />
      <SvgText x={200} y={152} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle">Δ log ΔP</SvgText>
      <SvgText x={245} y={126} fill={DT.varColor} fontSize="7" fontFamily={DT.mono}>Δ log Q</SvgText>
      <SvgText x={218} y={130} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} fontWeight="700">slope = n</SvgText>

      {/* Intercept indicator */}
      <Circle cx={ox} cy={205} r={5} fill="none" stroke={DT.varColor} strokeWidth="1.2" />
      <SvgText x={ox - 12} y={218} fill={DT.varColor} fontSize="7" fontFamily={DT.mono}>ln C</SvgText>

      {/* R² badge */}
      <Rect x={ox + axw - 60} y={oy - axh + 8} width={56} height={18} fill="rgba(0,0,0,0.6)" stroke={DT.leader} strokeWidth="0.8" />
      <SvgText x={ox + axw - 32} y={oy - axh + 20} fill={DT.leader} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">R² ≈ 0.99</SvgText>

      {/* Labels */}
      <LeaderLine x1={225} y1={108} x2={320} y2={48} varName="n" desc="flow exponent" anchor="end" />
      <LeaderLine x1={ox} y1={205} x2={25} y2={48} varName="C" desc="flow coefficient" anchor="start" />

      <FormulaTag formula="Q = C · ΔP^n    (n ≈ 0.5 orifice, ≈ 1.0 laminar; 0.6–0.7 typical)" />
    </DiagramCanvas>
  );
}
